/**
 * A3 — deliverable seed templates: parametric skeletons per building
 * archetype (levels + envelope + slab + roof + core partitions). The design
 * agent starts from a correct skeleton via yap_start_design and spends its
 * rounds on design decisions instead of boilerplate geometry.
 *
 * Each template is a list of yap_create_element argument bags, executed in
 * order. Coordinates are metres on a Z-up world; walls run on their
 * centerline; boundary loops are counterclockwise.
 */

export interface DesignTemplateElement {
    category: string;
    type?: string;
    name?: string;
    [key: string]: unknown;
}

export interface DesignTemplate {
    id: string;
    label: string;
    archetype: string;
    description: string;
    elements: DesignTemplateElement[];
}

const WALL = 'Basic Wall: Generic - 200mm';
const DOOR = 'Single-Flush: 0915 x 2134mm';
const WINDOW = 'Fixed: 0915 x 1830mm';

function storyEnvelope(
    prefix: string,
    levelName: string,
    w: number,
    d: number,
    height: number,
): DesignTemplateElement[] {
    return [
        { category: 'Walls', type: WALL, name: `${prefix} South Wall`, start: [0, 0, 0], end: [w, 0, 0], height, baseConstraintId: levelName },
        { category: 'Walls', type: WALL, name: `${prefix} East Wall`, start: [w, 0, 0], end: [w, d, 0], height, baseConstraintId: levelName },
        { category: 'Walls', type: WALL, name: `${prefix} North Wall`, start: [w, d, 0], end: [0, d, 0], height, baseConstraintId: levelName },
        { category: 'Walls', type: WALL, name: `${prefix} West Wall`, start: [0, d, 0], end: [0, 0, 0], height, baseConstraintId: levelName },
    ];
}

function slabAndRoof(w: number, d: number, levelName: string, roofType: string, slope?: number): DesignTemplateElement[] {
    return [
        {
            category: 'Floors', name: `${levelName} Slab`, boundary: [[0, 0], [w, 0], [w, d], [0, d]],
            levelId: levelName, thickness: 0.2,
        },
        ...(roofType === 'none' ? [] : [{
            category: 'Roofs', name: 'Main Roof',
            footprint: [[-0.5, -0.5], [w + 0.5, -0.5], [w + 0.5, d + 0.5], [-0.5, d + 0.5]],
            roofType, ...(slope !== undefined ? { slope } : {}), levelId: levelName, baseOffsetFromLevel: 3,
        }]),
    ];
}

export const DESIGN_TEMPLATES: DesignTemplate[] = [
    {
        id: 'single-story-house',
        label: 'Single-Story House',
        archetype: 'house',
        description: 'One-story 10×8 m house: Level 1, closed envelope, living/bed/bath partitions, slab, gable roof, entry + window openings. Adapt dimensions, openings and furniture to the program.',
        elements: [
            { category: 'Levels', name: 'Level 1', elevation: 0 },
            ...storyEnvelope('House', 'Level 1', 10, 8, 3),
            { category: 'Walls', type: WALL, name: 'Bedroom Partition', start: [6, 0, 0], end: [6, 5, 0], height: 3, baseConstraintId: 'Level 1' },
            { category: 'Walls', type: WALL, name: 'Bedroom Divider', start: [6, 5, 0], end: [10, 5, 0], height: 3, baseConstraintId: 'Level 1' },
            { category: 'Walls', type: WALL, name: 'Bath Partition', start: [8, 5, 0], end: [8, 8, 0], height: 3, baseConstraintId: 'Level 1' },
            ...slabAndRoof(10, 8, 'Level 1', 'gable', 25),
            { category: 'Doors', type: DOOR, name: 'Entry Door', hostWallId: 'House South Wall', position: 0.2, width: 1.0, height: 2.1 },
            { category: 'Doors', type: DOOR, name: 'Bedroom Door', hostWallId: 'Bedroom Partition', position: 0.8 },
            { category: 'Doors', type: DOOR, name: 'Bath Door', hostWallId: 'Bedroom Divider', position: 0.85 },
            { category: 'Windows', type: WINDOW, name: 'Living Window', hostWallId: 'House South Wall', position: 0.6, width: 1.8, sillHeight: 0.9 },
            { category: 'Windows', type: WINDOW, name: 'Bedroom Window', hostWallId: 'House East Wall', position: 0.5, width: 1.5, sillHeight: 0.9 },
        ],
    },
    {
        id: 'two-story-house',
        label: 'Two-Story House',
        archetype: 'house',
        description: 'Two-story 10×8 m house: Levels 1–2 (3 m stories), envelope per story, ground partitions, slabs, flat roof, entry + window openings. Stairs, facade detail and furniture are the agent\'s rounds.',
        elements: [
            { category: 'Levels', name: 'Level 1', elevation: 0 },
            { category: 'Levels', name: 'Level 2', elevation: 3200 },
            ...storyEnvelope('House', 'Level 1', 10, 8, 3),
            ...storyEnvelope('House L2', 'Level 2', 10, 8, 3),
            { category: 'Walls', type: WALL, name: 'Bedroom Partition', start: [6, 0, 0], end: [6, 5, 0], height: 3, baseConstraintId: 'Level 1' },
            { category: 'Walls', type: WALL, name: 'Bath Partition', start: [8, 5, 0], end: [8, 8, 0], height: 3, baseConstraintId: 'Level 1' },
            { category: 'Floors', name: 'Level 1 Slab', boundary: [[0, 0], [10, 0], [10, 8], [0, 8]], levelId: 'Level 1', thickness: 0.2 },
            { category: 'Floors', name: 'Level 2 Slab', boundary: [[0, 0], [10, 0], [10, 8], [0, 8]], levelId: 'Level 2', thickness: 0.2 },
            { category: 'Roofs', name: 'Main Roof', footprint: [[-0.5, -0.5], [10.5, -0.5], [10.5, 8.5], [-0.5, 8.5]], roofType: 'flat', levelId: 'Level 2', baseOffsetFromLevel: 3 },
            { category: 'Doors', type: DOOR, name: 'Entry Door', hostWallId: 'House South Wall', position: 0.2, width: 1.0, height: 2.1 },
            { category: 'Windows', type: WINDOW, name: 'Living Window', hostWallId: 'House South Wall', position: 0.6, width: 1.8, sillHeight: 0.9 },
            { category: 'Windows', type: WINDOW, name: 'Bedroom Window L1', hostWallId: 'House East Wall', position: 0.5, width: 1.5, sillHeight: 0.9 },
            { category: 'Windows', type: WINDOW, name: 'Bedroom Window L2', hostWallId: 'House L2 East Wall', position: 0.5, width: 1.5, sillHeight: 0.9 },
        ],
    },
    {
        id: 'studio-apartment',
        label: 'Studio Apartment',
        archetype: 'apartment',
        description: 'Compact 6×8 m studio: Level 1, envelope, bath enclosure, slab, flat roof, entry + window. Interior layout (kitchenette, sleeping zone) is the agent\'s rounds.',
        elements: [
            { category: 'Levels', name: 'Level 1', elevation: 0 },
            ...storyEnvelope('Studio', 'Level 1', 6, 8, 2.8),
            { category: 'Walls', type: WALL, name: 'Bath Partition', start: [3.5, 5.5, 0], end: [6, 5.5, 0], height: 2.8, baseConstraintId: 'Level 1' },
            { category: 'Walls', type: WALL, name: 'Bath Divider', start: [3.5, 5.5, 0], end: [3.5, 8, 0], height: 2.8, baseConstraintId: 'Level 1' },
            { category: 'Floors', name: 'Studio Slab', boundary: [[0, 0], [6, 0], [6, 8], [0, 8]], levelId: 'Level 1', thickness: 0.2 },
            { category: 'Roofs', name: 'Studio Roof', footprint: [[0, 0], [6, 0], [6, 8], [0, 8]], roofType: 'flat', levelId: 'Level 1', baseOffsetFromLevel: 2.8 },
            { category: 'Doors', type: DOOR, name: 'Entry Door', hostWallId: 'Studio South Wall', position: 0.75, width: 0.9, height: 2.1 },
            { category: 'Doors', type: DOOR, name: 'Bath Door', hostWallId: 'Bath Partition', position: 0.5 },
            { category: 'Windows', type: WINDOW, name: 'Studio Window', hostWallId: 'Studio South Wall', position: 0.3, width: 1.8, sillHeight: 0.9 },
        ],
    },
];

export function findDesignTemplate(id: string): DesignTemplate | undefined {
    const lowered = id.trim().toLowerCase();
    return DESIGN_TEMPLATES.find(
        (t) => t.id.toLowerCase() === lowered || t.label.toLowerCase() === lowered || t.archetype === lowered,
    );
}
